/**
 * The signature every round ends with (DECISIONS 2026-10-03): a person's route drawn on the map, in time order,
 * with a year and a note at every stop. Pin it draws three steps (came from → here → went next), Next stop the
 * whole journey, "Whose steps?" the whole life. One drawing for all three, plus the same route as text rows
 * in the panel (the accessible version of the map).
 */
import { h } from "../ui/dom";
import type { Line, Pin } from "../map/MapView";
import type { Place } from "../puzzles/types";
import type { Child } from "./types";

export interface RouteDraw {
  /** Stops in time order. */
  stops: Place[];
  color: string;
  /** Pin ids are idBase + index (tap a pin for its note). */
  idBase: number;
  /** Draw only the first n stops (the route growing stop by stop); default all. */
  upTo?: number;
  /** Indexes drawn by someone else (e.g. the answer marker): left out of the pins, still on the line. */
  skip?: readonly number[];
  /** Connect the stops with a line (false when their order isn't known). */
  line?: boolean;
  /** Test tag of the pins and the line (canvas data-pins / data-lines); default "route". */
  tag?: string;
}

/** Numbered pins and the connecting line for a route. */
export function routeLayers(r: RouteDraw): { pins: Pin[]; lines: Line[] } {
  const n = Math.min(r.upTo ?? r.stops.length, r.stops.length);
  const shown = r.stops.slice(0, n);
  const pins: Pin[] = shown.flatMap((s, i) =>
    r.skip?.includes(i) ? [] : [{ lat: s.lat, lng: s.lng, id: r.idBase + i, text: String(i + 1), color: r.color, label: s.name, sub: s.when ?? null, z: 2, tag: r.tag ?? "route" }],
  );
  const lines: Line[] = r.line !== false && shown.length > 1 ? [{ pts: shown.map((s) => [s.lat, s.lng] as [number, number]), color: r.color, width: 2.4, tag: r.tag ?? "route" }] : [];
  return { pins, lines };
}

/**
 * The route as text rows: number, name, year, note. `mark` = index of the stop the round was about
 * (ticked, with `markText` beside it). Rows carry data-route for tests.
 */
export function routeRows(stops: readonly Place[], o: { label?: string; mark?: number; markText?: string; kind: string }): Child {
  return [
    o.label ? h("div", { class: "kicker" }, h("span", null, o.label)) : null,
    h(
      "div",
      { class: "opts", role: "list", "aria-label": o.label ?? null, "data-route": o.kind },
      stops.map((r, i) => {
        const mark = i === o.mark;
        return h(
          "div",
          { class: `opt static${mark ? " good" : ""}`, role: "listitem" },
          h("span", { class: "lt", "aria-hidden": mark ? "true" : null }, mark ? "✓" : String(i + 1)),
          h("span", { class: "tx" }, r.name, r.when ? h("small", null, r.when) : null, mark && o.markText ? h("span", { class: "you" }, o.markText) : null, r.note ? h("span", { class: "note" }, r.note) : null),
        );
      }),
    ),
  ];
}
